import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Users, Plus, Search, Edit, Trash2, X, Save,
  CheckCircle, Building, Star, FileText, User,
  Briefcase, Calendar, Clock, Download, Upload,
  Eye, ChevronRight, Shield, Heart, Baby,
  Award, BookOpen, AlertCircle, RefreshCw,
  Paperclip, Hash, Image, Lock, TrendingUp,
  DollarSign, Phone, Mail, MapPin, Info,
  Filter, MoreVertical, Activity, Layers,
  XCircle, ChevronDown, ChevronUp, BarChart2
} from 'lucide-react';
import SearchableSelect from '../../shared/SearchableSelect';
import { MOSTRAR_BALANCE_PERMISOS } from '../../../utils/featureFlags';

/* ─── Paleta ProTalentHub ─── */
const C = {
  navy:    '#0f2d6e',
  blue:    '#1d4ed8',
  sky:     '#0ea5e9',
  gold:    '#f59e0b',
  green:   '#059669',
  red:     '#dc2626',
  purple:  '#7c3aed',
  orange:  '#ea580c',
  gray50:  '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray400: '#94a3b8',
  gray600: '#475569',
  gray800: '#1e293b',
  white:   '#ffffff',
};

// Mismos roles que requireHRStaff en el backend. Esto solo decide si se muestra la pestaña;
// el acceso real lo valida el servidor en /empleados/documentos-internos/*.
const ROLES_DTHS = ['admin', 'dths_director', 'dths_gerente', 'dths_coordinador', 'dths_analista'];
// Deben coincidir con MAX_MB_INTERNOS / MAX_ARCHIVOS_INTERNOS del backend (documentosInternosController.js).
const MAX_MB_INTERNOS = 100;
const MAX_ARCHIVOS_INTERNOS = 5;

const API_URL      = process.env.REACT_APP_API_URL;
const UPLOADS_URL  = API_URL ? API_URL.replace(/\/api\/?$/, '') : '';
const getToken = () => localStorage.getItem('dths_token');

const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || e.message || `HTTP ${res.status}`); }
  return res.json();
};

// Igual que apiFetch pero para consultas (GET) que no deben interrumpir el trabajo:
//  - reintenta solo, hasta 3 veces, ante un 404/502/503/504 o si el servidor no responde
//    (fallas momentáneas al repartir la petición entre las instancias del backend / IIS);
//  - NO reintenta errores reales (400, 401, 403, 500): esos se muestran de inmediato;
//  - el error trae `detalle` con el método, la ruta que vio el servidor, el código HTTP y la
//    instancia que respondió, para poder diagnosticar sin abrir las herramientas del navegador.
const ESTADOS_REINTENTABLES = [404, 502, 503, 504];
const apiGetConReintento = async (path, intentos = 3) => {
  let ultimo;
  for (let n = 1; n <= intentos; n++) {
    let reintentable = true;
    try {
      const res = await fetch(`${API_URL}${path}`, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) return await res.json();
      const cuerpo = await res.json().catch(() => ({}));
      ultimo = new Error(cuerpo.error || cuerpo.message || `HTTP ${res.status}`);
      ultimo.detalle = `GET ${cuerpo.path || path} -> HTTP ${res.status}`
        + (cuerpo.instance != null ? ` (instancia ${cuerpo.instance})` : '')
        + (n > 1 ? ` tras ${n} intentos` : '');
      reintentable = ESTADOS_REINTENTABLES.includes(res.status);
    } catch (e) {
      ultimo = new Error('No se pudo conectar con el servidor');
      ultimo.detalle = `GET ${path} -> sin respuesta (${e.message})` + (n > 1 ? ` tras ${n} intentos` : '');
    }
    if (!reintentable || n === intentos) break;
    await new Promise(r => setTimeout(r, 400 * n));
  }
  throw ultimo;
};

const fmtFecha = (f) => {
  if (!f) return '—';
  const [y, m, d] = f.split('T')[0].split('-');
  return new Date(y, m - 1, d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtMoney = (v) => v ? `RD$ ${Number(v).toLocaleString('es-DO')}` : '—';
const fmtBytes = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 ** 2).toFixed(1)} MB`;
};

/* ─── Toast ─── */
const Toast = ({ tipo, texto, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [onClose]);
  const ok = tipo === 'success';
  return (
    <div style={{
      position: 'fixed', top: 24, right: 24, zIndex: 9999,
      background: ok ? '#ecfdf5' : '#fef2f2',
      border: `1px solid ${ok ? '#6ee7b7' : '#fca5a5'}`,
      color: ok ? '#065f46' : '#991b1b',
      borderRadius: 12, padding: '14px 20px',
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 10px 25px rgba(0,0,0,.12)', maxWidth: 380,
    }}>
      {ok ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
      <span style={{ fontWeight: 500, fontSize: 14 }}>{texto}</span>
      <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}><X size={16} /></button>
    </div>
  );
};

/* ─── Badge Estado ─── */
const EstadoBadge = ({ activo }) => (
  <span style={{
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: activo ? '#dcfce7' : '#fee2e2',
    color: activo ? '#166534' : '#991b1b',
    border: `1px solid ${activo ? '#86efac' : '#fca5a5'}`,
  }}>{activo ? 'Activo' : 'Inactivo'}</span>
);

/* ════════════════════════════════════════════════════════════
   EXPEDIENTE DRAWER — Panel lateral completo
════════════════════════════════════════════════════════════ */
const ExpedienteDrawer = ({ empleado, onClose, departamentos, puestos, empleados, onSaved }) => {
  const { user } = useAuth();
  const esDTHS = ROLES_DTHS.includes(user?.rawRole);
  const [tab, setTab] = useState('perfil');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  /* sub-data */
  const [expediente,      setExpediente]      = useState(null);
  const [documentos,      setDocumentos]      = useState([]);
  const [historial,       setHistorial]       = useState([]);
  const [vacaciones,      setVacaciones]      = useState([]);
  const [balances,        setBalances]        = useState([]);
  const [permisos,        setPermisos]        = useState([]);
  const [balancePermisos, setBalancePermisos] = useState([]);
  const [contactoEmerg,   setContactoEmerg]   = useState(null);

  // ── FIX 1: estado para catálogos (necesario para TabFichaRRHH) ──
  const [catalogos, setCatalogos] = useState({
    sexos: [], generos: [], estadosCivil: [],
    tiposContrato: [], jornadas: [], turnos: [],
  });

  /* errores por sección */
  const [docError,      setDocError]      = useState(null);
  const [histError,     setHistError]     = useState(null);
  const [vacaError,     setVacaError]     = useState(null);
  const [permError,     setPermError]     = useState(null);
  const [postulacion,   setPostulacion]   = useState(null);
  const [postError,     setPostError]     = useState(null);
  const [contactoError, setContactoError] = useState(null);

  /* upload */
  const fileRef = useRef();
  const [dragOver,   setDragOver]   = useState(false);
  const [uploading,  setUploading]  = useState(false);

  const msg = (tipo, texto) => setToast({ tipo, texto });

  useEffect(() => {
    if (empleado) loadAll();
  }, [empleado]);

  // ── FIX 2: loadCatalogos agregado al Promise.allSettled ──
  const loadAll = async () => {
    setLoading(true);
    await Promise.allSettled([
      loadExpediente(),
      loadHistorial(),
      loadVacaciones(),
      loadPermisos(),
      loadPostulacion(),
      loadContactoEmergencia(),
      loadCatalogos(),          // ← NUEVO
    ]);
    setLoading(false);
  };

  // ── FIX 2b: función loadCatalogos ──
  const loadCatalogos = async () => {
    try {
      const d = await apiFetch('/empleados/catalogos');
      setCatalogos({
        sexos:         d.sexos         || [],
        generos:       d.generos       || [],
        estadosCivil:  d.estadosCivil  || [],
        tiposContrato: d.tiposContrato || [],
        jornadas:      d.jornadas      || [],
        turnos:        d.turnos        || [],
      });
    } catch { /* silencioso — no bloquea el resto */ }
  };

  const loadExpediente = async () => {
    setDocError(null);
    try {
      const d = await apiFetch(`/empleados/${empleado.id}/expediente`);
      setExpediente(d.expediente);
      if (d.expediente?.ExpedienteID) {
        const docs = await apiFetch(`/empleados/expediente/${d.expediente.ExpedienteID}/documentos`);
        const lista = docs.documentos || docs.data || (Array.isArray(docs) ? docs : []);
        setDocumentos(lista);
      } else {
        setDocumentos([]);
      }
    } catch (e) {
      setExpediente(null);
      setDocumentos([]);
      setDocError(e.message);
    }
  };

  const loadHistorial = async () => {
    setHistError(null);
    try {
      const d = await apiFetch(`/empleados/${empleado.id}/acciones`);
      const lista = d.acciones || d.historial || d.data || (Array.isArray(d) ? d : []);
      setHistorial(lista);
    } catch (e) {
      setHistorial([]);
      setHistError(e.message);
    }
  };

  const loadVacaciones = async () => {
    setVacaError(null);
    try {
      const d = await apiFetch(`/empleados/${empleado.id}/vacaciones`);
      const lista = d.solicitudes || d.vacaciones || d.data || (Array.isArray(d) ? d : []);
      setVacaciones(lista);
      setBalances(d.balances || []);
    } catch (e) {
      setVacaciones([]);
      setBalances([]);
      setVacaError(e.message);
    }
  };

  const loadPermisos = async () => {
    setPermError(null);
    try {
      const d = await apiFetch(`/empleados/${empleado.id}/permisos`);
      const lista = d.permisos || d.solicitudes || d.data || (Array.isArray(d) ? d : []);
      setPermisos(lista);
      setBalancePermisos(d.balancePermisos || []);
    } catch (e) {
      setPermisos([]);
      setPermError(e.message);
    }
  };

  const loadContactoEmergencia = async () => {
    setContactoError(null);
    try {
      const d = await apiFetch(`/empleados/${empleado.id}/contacto-emergencia`);
      setContactoEmerg(d.contacto || d.data || d || null);
    } catch (e) {
      setContactoEmerg(null);
      setContactoError(e.message);
    }
  };

  const loadPostulacion = async () => {
    setPostError(null);
    try {
      const d = await apiFetch(`/empleados/${empleado.id}/postulacion`);
      setPostulacion(d.postulacion || null);
    } catch (e) {
      setPostulacion(null);
      setPostError(e.message);
    }
  };

  const uploadDoc = async (files) => {
    if (!files?.length) return;
    if (!expediente?.ExpedienteID) { msg('error', 'El empleado no tiene expediente creado'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('documentos', f));
      const res = await fetch(`${API_URL}/empleados/expediente/${expediente.ExpedienteID}/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
      });
      if (!res.ok) throw new Error('Error al subir documento');
      await loadExpediente();
      msg('success', 'Documento(s) subido(s) correctamente');
    } catch (e) { msg('error', e.message); } finally { setUploading(false); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('¿Eliminar este documento del expediente?')) return;
    try {
      await apiFetch(`/empleados/documento/${docId}`, { method: 'DELETE' });
      await loadExpediente();
      msg('success', 'Documento eliminado');
    } catch (e) { msg('error', e.message); }
  };

  // ── FIX 3: 'ficha' agregado al array TABS ──
  const TABS = [
    { id: 'perfil',     label: 'Perfil',            icon: User },
    { id: 'ficha',      label: 'Ficha',             icon: BookOpen },   // ← NUEVO
    { id: 'documentos', label: 'Documentos',        icon: FileText, badge: documentos.length },
    ...(esDTHS ? [{ id: 'docsInternos', label: 'Docs. Internos DTHS', icon: Lock }] : []),
    { id: 'historial', label: 'Historial Laboral', icon: Briefcase, badge: historial.length },
    { id: 'vacaciones', label: 'Vacaciones',        icon: Calendar,  badge: vacaciones.length },
    { id: 'permisos',   label: 'Licencias/Permisos',icon: Shield,    badge: permisos.length },
    { id: 'emergencia', label: 'Emergencia',        icon: Heart },
    { id: 'formacion',  label: 'Formación',         icon: Award },
  ];

  const initials = `${empleado?.nombre?.charAt(0) || ''}${empleado?.apellido?.charAt(0) || ''}`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex' }}>
      {/* overlay */}
      <div style={{ flex: 1, background: 'rgba(15,23,42,.6)', backdropFilter: 'blur(4px)' }} onClick={onClose} />

      {/* drawer */}
      <div style={{
        width: 'min(860px, 95vw)', height: '100vh', background: C.white,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-12px 0 60px rgba(0,0,0,.2)',
        animation: 'slideIn .3s cubic-bezier(.16,1,.3,1)',
      }}>
        <style>{`
          @keyframes slideIn { from { transform:translateX(100%); } to { transform:translateX(0); } }
          @keyframes fadeUp  { from { transform:translateY(10px); opacity:0; } to { transform:translateY(0); opacity:1; } }
          @keyframes spin    { to   { transform:rotate(360deg); } }
        `}</style>

        {toast && <Toast tipo={toast.tipo} texto={toast.texto} onClose={() => setToast(null)} />}

        {/* ── HEADER ── */}
        <div style={{
          background: `linear-gradient(135deg, ${C.navy} 0%, #1a3a8f 50%, ${C.blue} 100%)`,
          padding: '24px 28px', flexShrink: 0, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,.04)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, position: 'relative', zIndex: 1 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', overflow: 'hidden',
              background: `linear-gradient(135deg, ${C.sky}, ${C.blue})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 700, color: C.white,
              border: '3px solid rgba(255,255,255,.25)', flexShrink: 0,
              boxShadow: '0 4px 16px rgba(0,0,0,.3)',
            }}>
              {empleado?.foto && !empleado.foto.includes('placeholder') ? (
                <img src={empleado.foto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display='none'; }} />
              ) : initials}
            </div>
            <div style={{ flex: 1, color: C.white }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-.3px' }}>
                  {empleado?.nombreCompleto || `${empleado?.nombre} ${empleado?.apellido}`}
                </h2>
                <span style={{ background: 'rgba(255,255,255,.18)', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                  EMP{String(empleado?.id).padStart(3, '0')}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 18, fontSize: 13, opacity: .8, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Briefcase size={12} />{empleado?.puesto || '—'}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Building size={12} />{empleado?.departamento || '—'}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={12} />Ingreso: {fmtFecha(empleado?.fechaIngreso)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <EstadoBadge activo={empleado?.estado === 'activo'} />
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', borderRadius: 8, padding: 8, cursor: 'pointer', color: C.white, display: 'flex' }}><X size={18} /></button>
            </div>
          </div>

          {expediente && (
            <div style={{ marginTop: 16, display: 'flex', gap: 20, position: 'relative', zIndex: 1 }}>
              {[
                { label: 'N° Expediente', value: expediente.NumeroExpediente || `EXP-${empleado.id}` },
                { label: 'Documentos',    value: documentos.length },
                { label: 'Estado',        value: expediente.Estado || 'Activo' },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,.12)', padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,.2)' }}>
                  <div style={{ fontSize: 10, opacity: .7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5, color: C.white }}>{s.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.white }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── TABS ── */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.gray200}`, background: C.gray50, flexShrink: 0, overflowX: 'auto' }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '12px 18px', border: 'none', background: 'transparent',
                cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                color: active ? C.blue : C.gray600,
                borderBottom: `2px solid ${active ? C.blue : 'transparent'}`,
                display: 'flex', alignItems: 'center', gap: 6, transition: 'all .15s',
              }}>
                <Icon size={14} />{t.label}
                {t.badge > 0 && (
                  <span style={{ fontSize: 10, background: active ? C.blue : C.gray200, color: active ? C.white : C.gray600, borderRadius: 20, padding: '1px 6px', fontWeight: 800 }}>{t.badge}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── CONTENIDO ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <div style={{ width: 36, height: 36, border: `3px solid ${C.gray200}`, borderTop: `3px solid ${C.blue}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <>
              {tab === 'perfil'     && <TabPerfil empleado={empleado} departamentos={departamentos} puestos={puestos} empleados={empleados} onSaved={() => { onSaved?.(); loadAll(); }} />}
              {/* FIX 4: render del tab Ficha ── */}
              {tab === 'ficha'      && <TabFichaRRHH empleado={empleado} catalogos={catalogos} onSaved={() => { onSaved?.(); loadAll(); }} />}
              {tab === 'documentos' && <TabDocumentos docs={documentos} onUpload={uploadDoc} onDelete={deleteDoc} dragOver={dragOver} setDragOver={setDragOver} fileRef={fileRef} uploading={uploading} expediente={expediente} error={docError} />}
              {tab === 'docsInternos' && esDTHS && <TabDocumentosInternos empleadoId={empleado.id} msg={msg} />}
              {tab === 'historial'  && <TabHistorial historial={historial} error={histError} />}
              {tab === 'vacaciones' && <TabVacaciones solicitudes={vacaciones} balances={balances} error={vacaError} />}
              {tab === 'permisos'   && <TabPermisos permisos={permisos} balancePermisos={balancePermisos} error={permError} />}
              {tab === 'formacion'  && <TabFormacion postulacion={postulacion} error={postError} />}
              {tab === 'emergencia' && <TabEmergenciaRRHH empleadoId={empleado.id} contacto={contactoEmerg} error={contactoError} onSaved={loadAll} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TAB: PERFIL (editable por RRHH)
════════════════════════════════════════════════════════════ */
const TabPerfil = ({ empleado, departamentos, puestos, empleados, onSaved }) => {
  const [editMode, setEditMode]   = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast]         = useState(null);
  const [catalogos, setCatalogos] = useState({
    tiposContrato: [], jornadas: [], turnos: [],
    sexos: [], generos: [], estadosCivil: [],
  });
  const fotoRef = useRef();

  const [f, setF] = useState({
    nombre:              empleado?.nombre             || '',
    apellido:            empleado?.apellido           || '',
    cedula:              empleado?.cedula             || '',
    email:               empleado?.email              || '',
    emailPersonal:       empleado?.emailPersonal      || '',
    telefono:            empleado?.telefono           || '',
    telefono2:           empleado?.telefono2          || '',
    direccion:           empleado?.direccion          || '',
    direccion2:          empleado?.direccion2         || '',
    cargo:               empleado?.puesto             || '',
    salario:             empleado?.salario            || '',
    fechaIngreso:        empleado?.fechaIngreso       || '',
    fechaNacimiento:     empleado?.fechaNacimiento    || '',
    sexo:                empleado?.sexo               || '',
    genero:              empleado?.genero             || '',
    estadoCivil:         empleado?.estadoCivil        || '',
    estado:              empleado?.estado === 'activo' ? 1 : 0,
    departamentoID:      empleado?.departamentoId     || '',
    puestoID:            empleado?.puestoId           || '',
    supervisorID:        empleado?.supervisorId       || '',
    supervisorInmediato: empleado?.supervisorInmediato|| '',
    tipoContrato:        empleado?.tipoContrato       || '',
    jornada:             empleado?.jornada            || '',
    turno:               empleado?.turno              || '',
  });

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const msg = (tipo, texto) => setToast({ tipo, texto });

  useEffect(() => {
    apiFetch('/empleados/catalogos')
      .then(d => setCatalogos({
        tiposContrato: d.tiposContrato || [],
        jornadas:      d.jornadas      || [],
        turnos:        d.turnos        || [],
        sexos:         d.sexos         || [],
        generos:       d.generos       || [],
        estadosCivil:  d.estadosCivil  || [],
      }))
      .catch(e => console.error('getCatalogos error:', e.message));
  }, []);

  const puestosDepto = useMemo(() => {
    if (!f.departamentoID) return puestos || [];
    const id = parseInt(f.departamentoID);
    const filtrados = (puestos || []).filter(p => p.departamentoId === id);
    return filtrados.length > 0 ? filtrados : (puestos || []);
  }, [f.departamentoID, puestos]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await apiFetch(`/empleados/update/${empleado.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nombre:              f.nombre,
          apellido:            f.apellido,
          cedula:              f.cedula,
          email:               f.email,
          emailPersonal:       f.emailPersonal,
          telefono:            f.telefono,
          telefono2:           f.telefono2,
          direccion:           f.direccion,
          direccion2:          f.direccion2,
          cargo:               f.cargo,
          salario:             parseFloat(f.salario) || 0,
          fechaIngreso:        f.fechaIngreso        || null,
          fechaNacimiento:     f.fechaNacimiento     || null,
          sexo:                f.sexo                || null,
          genero:              f.genero              || null,
          estadoCivil:         f.estadoCivil         || null,
          estado:              parseInt(f.estado),
          departamentoID:      parseInt(f.departamentoID)  || null,
          puestoID:            parseInt(f.puestoID)        || null,
          supervisorID:        parseInt(f.supervisorID)    || null,
          supervisorInmediato: f.supervisorInmediato       || null,
          tipoContrato:        f.tipoContrato              || null,
          jornada:             f.jornada                   || null,
          turno:               f.turno                     || null,
        }),
      });
      msg('success', 'Perfil actualizado correctamente');
      setEditMode(false);
      onSaved?.();
    } catch (e) {
      msg('error', e.message);
    } finally {
      setGuardando(false);
    }
  };

  const subirFoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('foto', file);
    try {
      const res = await fetch(`${API_URL}/empleados/${empleado.id}/foto`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Error al subir foto');
      msg('success', 'Foto actualizada');
      onSaved?.();
    } catch (e) {
      msg('error', e.message);
    }
  };

  const initials = `${empleado?.nombre?.charAt(0) || ''}${empleado?.apellido?.charAt(0) || ''}`;

  const CatSelect = ({ label, field, opts }) => (
    <div>
      <label style={LBL}>{label}</label>
      <select value={f[field] || ''} onChange={e => upd(field, e.target.value)} style={SEL_FULL}>
        <option value="">Seleccionar</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ animation: 'fadeUp .25s ease', display: 'grid', gap: 16 }}>
      {toast && <Toast tipo={toast.tipo} texto={toast.texto} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {editMode ? (
          <>
            <button onClick={() => setEditMode(false)} style={{ padding: '8px 16px', background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.gray600 }}>Cancelar</button>
            <button onClick={guardar} disabled={guardando} style={{ padding: '8px 16px', background: C.blue, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Save size={13} />{guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </>
        ) : (
          <button onClick={() => setEditMode(true)} style={{ padding: '8px 16px', background: C.navy, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Edit size={13} />Editar perfil
          </button>
        )}
      </div>

      <SeccionCard title="Foto de Perfil" icon={User}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', background: `linear-gradient(135deg, ${C.sky}, ${C.blue})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: C.white, flexShrink: 0 }}>
            {empleado?.foto ? <img src={empleado.foto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} /> : initials}
          </div>
          <div>
            <p style={{ margin: '0 0 8px', fontSize: 13, color: C.gray600 }}>Foto actual del colaborador</p>
            <button onClick={() => fotoRef.current?.click()} style={{ padding: '7px 14px', background: C.gray100, border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: C.gray700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Upload size={12} />Cambiar foto
            </button>
            <input ref={fotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => subirFoto(e.target.files[0])} />
          </div>
        </div>
      </SeccionCard>

      <SeccionCard title="Información Personal" icon={User}>
        {editMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            {[
              { label: 'Nombre *',           key: 'nombre' },
              { label: 'Apellido *',         key: 'apellido' },
              { label: 'Cédula',             key: 'cedula' },
              { label: 'Email corporativo',  key: 'email',           type: 'email' },
              { label: 'Email personal',     key: 'emailPersonal',   type: 'email' },
              { label: 'Teléfono principal', key: 'telefono',        type: 'tel' },
              { label: 'Teléfono 2',         key: 'telefono2',       type: 'tel' },
              { label: 'Fecha Nacimiento',   key: 'fechaNacimiento', type: 'date' },
            ].map(field => (
              <div key={field.key}>
                <label style={LBL}>{field.label}</label>
                <input type={field.type || 'text'} value={f[field.key] || ''} onChange={e => upd(field.key, e.target.value)} style={INP} />
              </div>
            ))}
            <CatSelect label="Sexo"         field="sexo"        opts={catalogos.sexos} />
            <CatSelect label="Género"       field="genero"      opts={catalogos.generos} />
            <CatSelect label="Estado Civil" field="estadoCivil" opts={catalogos.estadosCivil} />
            <div style={{ gridColumn: '1/-1' }}>
              <label style={LBL}>Dirección principal</label>
              <textarea value={f.direccion || ''} onChange={e => upd('direccion', e.target.value)} style={{ ...INP, minHeight: 70, resize: 'vertical' }} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={LBL}>Dirección secundaria</label>
              <textarea value={f.direccion2 || ''} onChange={e => upd('direccion2', e.target.value)} style={{ ...INP, minHeight: 70, resize: 'vertical' }} />
            </div>
          </div>
        ) : (
          <Grid cols={2}>
            <Dato label="Nombre"           value={empleado?.nombre} />
            <Dato label="Apellido"         value={empleado?.apellido} />
            <Dato label="Cédula / ID"      value={empleado?.cedula} />
            <Dato label="Sexo"             value={empleado?.sexo} />
            <Dato label="Género"           value={empleado?.genero} />
            <Dato label="Estado Civil"     value={empleado?.estadoCivil} />
            <Dato label="Fecha Nacimiento" value={fmtFecha(empleado?.fechaNacimiento)} />
            <Dato label="Email corp."      value={empleado?.email} />
            <Dato label="Email personal"   value={empleado?.emailPersonal} />
            <Dato label="Teléfono"         value={empleado?.telefono} />
            <Dato label="Teléfono 2"       value={empleado?.telefono2} />
            <Dato label="Estado"           value={<EstadoBadge activo={empleado?.estado === 'activo'} />} />
            <Dato label="Dirección"        value={empleado?.direccion}  full />
            <Dato label="Dirección 2"      value={empleado?.direccion2} full />
          </Grid>
        )}
      </SeccionCard>

      <SeccionCard title="Información Laboral" icon={Briefcase}>
        {editMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div>
              <label style={LBL}>Departamento</label>
              <SearchableSelect
                value={f.departamentoID}
                onChange={v => { upd('departamentoID', v); upd('puestoID', ''); }}
                options={(departamentos || []).map(d => ({ id: d.id, label: d.nombre }))}
                placeholder="Buscar departamento..."
                emptyLabel="Seleccionar"
                inputStyle={SEL_FULL}
              />
            </div>
            <div>
              <label style={LBL}>Puesto</label>
              <SearchableSelect
                value={f.puestoID}
                onChange={v => upd('puestoID', v)}
                options={puestosDepto.map(p => ({ id: p.id, label: p.nombre }))}
                placeholder="Buscar puesto..."
                emptyLabel="Seleccionar"
                inputStyle={SEL_FULL}
              />
            </div>
            <div>
              <label style={LBL}>Cargo / Título</label>
              <input type="text" value={f.cargo || ''} onChange={e => upd('cargo', e.target.value)} style={INP} />
            </div>
            <div>
              <label style={LBL}>Supervisor Inmediato</label>
              <SearchableSelect
                value={f.supervisorID}
                onChange={v => {
                  const s = (empleados || []).find(em => em.id === parseInt(v));
                  upd('supervisorID', v);
                  upd('supervisorInmediato', s ? `${s.nombre} ${s.apellido}`.trim() : '');
                }}
                options={(empleados || []).filter(em => em.id !== empleado?.id).sort((a, b) => a.nombre.localeCompare(b.nombre)).map(em => ({ id: em.id, label: `${em.nombre} ${em.apellido}` }))}
                placeholder="Buscar supervisor..."
                emptyLabel="Sin supervisor"
                inputStyle={SEL_FULL}
              />
            </div>
            <div>
              <label style={LBL}>Fecha de Ingreso</label>
              <input type="date" value={f.fechaIngreso || ''} onChange={e => upd('fechaIngreso', e.target.value)} style={INP} />
            </div>
            <div>
              <label style={LBL}>Salario (RD$)</label>
              <input type="number" value={f.salario || ''} onChange={e => upd('salario', e.target.value)} style={INP} />
            </div>
            <CatSelect label="Tipo de Contrato" field="tipoContrato" opts={catalogos.tiposContrato} />
            <CatSelect label="Jornada"           field="jornada"      opts={catalogos.jornadas} />
            <CatSelect label="Turno"             field="turno"        opts={catalogos.turnos} />
            <div>
              <label style={LBL}>Estado</label>
              <select value={f.estado} onChange={e => upd('estado', e.target.value)} style={SEL_FULL}>
                <option value={1}>Activo</option>
                <option value={0}>Inactivo</option>
              </select>
            </div>
          </div>
        ) : (
          <Grid cols={2}>
            <Dato label="Departamento"   value={empleado?.departamento} />
            <Dato label="Puesto / Cargo" value={empleado?.puesto} />
            <Dato label="Fecha Ingreso"  value={fmtFecha(empleado?.fechaIngreso)} />
            <Dato label="Salario"        value={fmtMoney(empleado?.salario)} />
            <Dato label="Tipo Contrato"  value={empleado?.tipoContrato} />
            <Dato label="Jornada"        value={empleado?.jornada} />
            <Dato label="Turno"          value={empleado?.turno} />
            <Dato label="Estado"         value={<EstadoBadge activo={empleado?.estado === 'activo'} />} />
            <Dato label="Supervisor"     value={empleado?.supervisorInmediato} />
            <Dato label="Código"         value={`EMP${String(empleado?.id).padStart(3, '0')}`} />
          </Grid>
        )}
      </SeccionCard>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────
   ErrorPanel
──────────────────────────────────────────────────────────── */
const ErrorPanel = ({ mensaje, endpoint }) => (
  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '16px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
    <AlertCircle size={20} color='#dc2626' style={{ flexShrink: 0, marginTop: 1 }} />
    <div>
      <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 13, color: '#991b1b' }}>Error al cargar datos</p>
      <p style={{ margin: '0 0 6px', fontSize: 13, color: '#7f1d1d' }}>{mensaje}</p>
      {endpoint && <code style={{ fontSize: 11, background: '#fee2e2', padding: '2px 6px', borderRadius: 4, color: '#991b1b' }}>{endpoint}</code>}
      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#9a3412' }}>Verifica que el endpoint existe en el backend y que el token es válido.</p>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════
   TAB: DOCUMENTOS
════════════════════════════════════════════════════════════ */
const TabDocumentos = ({ docs, onUpload, onDelete, dragOver, setDragOver, fileRef, uploading, expediente, error }) => {
  const EXT_ICON = (nombre) => {
    const ext = (nombre || '').split('.').pop().toLowerCase();
    if (['jpg','jpeg','png','gif','webp'].includes(ext)) return { icon: Image, color: C.purple };
    if (ext === 'pdf') return { icon: FileText, color: '#dc2626' };
    return { icon: Paperclip, color: C.blue };
  };

  const normUrl = (raw) => {
    if (!raw) return '';
    let u = raw.replace(/\\/g, '/');
    if (!u.startsWith('/') && !u.startsWith('http')) u = '/' + u;
    return u;
  };

  const normDoc = (doc) => {
    const rawUrl  = doc.URLArchivo  || doc.urlArchivo  || doc.url  || '';
    const rawRuta = doc.RutaArchivo || doc.rutaArchivo || doc.ruta || '';
    const nombre  = doc.NombreArchivo || doc.nombreArchivo || '';
    let url = normUrl(rawUrl) || normUrl(rawRuta);
    if (!url && nombre) url = `/uploads/documentos/${nombre}`;
    return {
      id:       doc.DocumentoID || doc.documentoId || doc.id,
      nombre:   doc.NombreOriginal || doc.nombreOriginal || doc.NombreArchivo || doc.nombre || 'Archivo',
      url,
      categoria:doc.Categoria || doc.categoria || doc.NombreCategoria || 'Documento',
      fecha:    doc.FechaCarga || doc.fechaCarga || doc.fechaSubida || doc.FechaSubida || '',
      tamano:   doc.Tamaño || doc.tamaño || doc.Tamano || doc.tamano || doc.size || 0,
      auto:     doc.Automatico === true || doc.Automatico === 1 || false,
    };
  };

  return (
    <div style={{ animation: 'fadeUp .25s ease' }}>
      {error && <div style={{ marginBottom: 16 }}><ErrorPanel mensaje={error} endpoint={`/empleados/:id/expediente  →  /empleados/expediente/:id/documentos`} /></div>}
      {!error && !expediente && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10 }}>
          <AlertCircle size={16} color='#c2410c' style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#9a3412' }}>Este empleado aún no tiene expediente. Contacta a RRHH para crearlo.</p>
        </div>
      )}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); onUpload(e.dataTransfer.files); }}
        onClick={() => expediente && fileRef.current?.click()}
        style={{ border: `2px dashed ${dragOver ? C.blue : C.gray200}`, borderRadius: 12, padding: '2rem', textAlign: 'center', cursor: expediente ? 'pointer' : 'not-allowed', background: dragOver ? '#eff6ff' : C.gray50, transition: 'all .2s', marginBottom: 20, opacity: expediente ? 1 : .5 }}
      >
        <Upload size={32} color={dragOver ? C.blue : C.gray400} style={{ margin: '0 auto 10px', display: 'block' }} />
        <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: C.gray800 }}>{uploading ? 'Subiendo...' : dragOver ? 'Suelta aquí' : 'Arrastra archivos o haz clic'}</p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: C.gray600 }}>PDF, DOC, DOCX, JPG, PNG — máx. 10MB</p>
        <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => onUpload(e.target.files)} style={{ display: 'none' }} />
      </div>
      {docs.length === 0 ? (
        <EmptyState icon={FileText} title="Sin documentos" desc="No hay documentos en este expediente" />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {docs.map((docRaw, i) => {
            const doc = normDoc(docRaw);
            const { icon: Icon, color } = EXT_ICON(doc.nombre);
            return (
              <div key={doc.id || i} style={{ display: 'flex', alignItems: 'center', gap: 14, background: C.white, borderRadius: 10, padding: '14px 16px', border: `1px solid ${C.gray200}`, boxShadow: '0 1px 3px rgba(0,0,0,.04)', animation: `fadeUp .3s ease ${i * 40}ms both` }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={18} color={color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: C.gray800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.nombre}</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.gray600 }}>{doc.categoria} · {fmtFecha(doc.fecha)} · {fmtBytes(doc.tamano)}</p>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {doc.url ? (
                    <a href={`${UPLOADS_URL}${doc.url}`} target="_blank" rel="noreferrer" title="Ver documento" style={{ padding: 7, background: '#eff6ff', border: `1px solid #bfdbfe`, borderRadius: 7, color: C.blue, display: 'flex', alignItems: 'center', textDecoration: 'none' }}><Eye size={14} /></a>
                  ) : (
                    <span title="URL no disponible" style={{ padding: 7, background: C.gray100, border: `1px solid ${C.gray200}`, borderRadius: 7, color: C.gray400, display: 'flex', alignItems: 'center', cursor: 'not-allowed' }}><Eye size={14} /></span>
                  )}
                  {doc.url ? (
                    <a href={`${UPLOADS_URL}${doc.url}`} download={doc.nombre} title="Descargar" style={{ padding: 7, background: `${C.green}15`, border: `1px solid ${C.green}44`, borderRadius: 7, color: C.green, display: 'flex', alignItems: 'center', textDecoration: 'none' }}><Download size={14} /></a>
                  ) : (
                    <span title="Sin archivo" style={{ padding: 7, background: C.gray100, border: `1px solid ${C.gray200}`, borderRadius: 7, color: C.gray400, display: 'flex', alignItems: 'center', cursor: 'not-allowed' }}><Download size={14} /></span>
                  )}
                  {!doc.auto && (
                    <button onClick={() => onDelete(doc.id)} title="Eliminar" style={{ padding: 7, background: `${C.red}12`, border: `1px solid ${C.red}33`, borderRadius: 7, color: C.red, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TAB: DOCUMENTOS INTERNOS DTHS
   Solo se monta para roles DTHS. Descarga/visualización con fetch + token
   (los archivos no están en /uploads, no hay URL pública).
════════════════════════════════════════════════════════════ */
const TabDocumentosInternos = ({ empleadoId, msg }) => {
  const [docs,        setDocs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [dragOver,    setDragOver]    = useState(false);
  const [descripcion, setDescripcion] = useState('');
  const fileRef = useRef();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGetConReintento(`/empleados/documentos-internos/empleado/${empleadoId}`);
      setDocs(d.documentos || []);
      setError(null);
    } catch (e) {
      setDocs([]);
      setError({ mensaje: e.message, detalle: e.detalle || '' });
    } finally {
      setLoading(false);
    }
  }, [empleadoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = async (files) => {
    if (!files?.length) return;
    const lista = Array.from(files);
    if (lista.length > MAX_ARCHIVOS_INTERNOS) { msg('error', `Máximo ${MAX_ARCHIVOS_INTERNOS} archivos por subida`); return; }
    const grande = lista.find(f => f.size > MAX_MB_INTERNOS * 1024 * 1024);
    if (grande) { msg('error', `"${grande.name}" supera el máximo de ${MAX_MB_INTERNOS}MB`); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      lista.forEach(f => fd.append('documentos', f));
      if (descripcion.trim()) fd.append('descripcion', descripcion.trim());
      const res = await fetch(`${API_URL}/empleados/documentos-internos/empleado/${empleadoId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      setDescripcion('');
      msg('success', data.message || 'Documento(s) subido(s)');
      await cargar();
    } catch (e) {
      msg('error', e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const abrir = async (doc) => {
    // PDF e imágenes se ven en una pestaña nueva; el resto se descarga.
    const verEnPestana = /\.(pdf|jpe?g|png)$/i.test(doc.nombre);
    const pestana = verEnPestana ? window.open('', '_blank') : null;
    try {
      const res = await fetch(`${API_URL}/empleados/documentos-internos/${doc.id}/archivo`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || e.message || `HTTP ${res.status}`);
      }
      const blobUrl = URL.createObjectURL(await res.blob());
      if (pestana) {
        pestana.location.href = blobUrl;
      } else {
        const a = document.createElement('a');
        a.href = blobUrl; a.download = doc.nombre;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      if (pestana) pestana.close();
      msg('error', e.message);
    }
  };

  const eliminar = async (doc) => {
    if (!window.confirm(`¿Eliminar "${doc.nombre}" de los documentos internos?`)) return;
    try {
      await apiFetch(`/empleados/documentos-internos/${doc.id}`, { method: 'DELETE' });
      msg('success', 'Documento eliminado');
      await cargar();
    } catch (e) {
      msg('error', e.message);
    }
  };

  return (
    <div style={{ animation: 'fadeUp .25s ease' }}>
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10 }}>
        <Lock size={16} color='#b45309' style={{ flexShrink: 0, marginTop: 1 }} />
        <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
          Documentos internos de DTHS. Solo el personal de Talento Humano puede verlos; el colaborador no tiene acceso a esta sección.
        </p>
      </div>

      {error && (
        <div role="alert" style={{ marginBottom: 16, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <AlertCircle size={20} color='#dc2626' style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 13, color: '#991b1b' }}>No se pudieron cargar los documentos internos</p>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: '#7f1d1d' }}>{error.mensaje}</p>
            {error.detalle && <code style={{ display: 'block', fontSize: 11, background: '#fee2e2', padding: '3px 6px', borderRadius: 4, color: '#991b1b', overflowWrap: 'anywhere' }}>{error.detalle}</code>}
            <button onClick={cargar} disabled={loading} style={{ marginTop: 10, padding: '6px 14px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#991b1b', fontWeight: 600, fontSize: 12, cursor: loading ? 'wait' : 'pointer' }}>
              {loading ? 'Reintentando...' : 'Reintentar'}
            </button>
          </div>
        </div>
      )}

      <input
        type="text" value={descripcion} onChange={e => setDescripcion(e.target.value)} maxLength={500}
        placeholder="Descripción o nota (opcional, se aplica a los archivos que subas ahora)"
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginBottom: 10, border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 13, color: C.gray800 }}
      />
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); subir(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        style={{ border: `2px dashed ${dragOver ? C.blue : C.gray200}`, borderRadius: 12, padding: '2rem', textAlign: 'center', cursor: 'pointer', background: dragOver ? '#eff6ff' : C.gray50, transition: 'all .2s', marginBottom: 20 }}
      >
        <Upload size={32} color={dragOver ? C.blue : C.gray400} style={{ margin: '0 auto 10px', display: 'block' }} />
        <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: C.gray800 }}>{uploading ? 'Subiendo...' : dragOver ? 'Suelta aquí' : 'Arrastra archivos o haz clic'}</p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: C.gray600 }}>PDF, DOC, DOCX, XLS, XLSX, JPG, PNG — máx. {MAX_MB_INTERNOS}MB por archivo, hasta {MAX_ARCHIVOS_INTERNOS} archivos</p>
        <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={e => subir(e.target.files)} style={{ display: 'none' }} />
      </div>

      {loading ? (
        <p style={{ fontSize: 13, color: C.gray600 }}>Cargando...</p>
      ) : docs.length === 0 && !error ? (
        <EmptyState icon={Lock} title="Sin documentos internos" desc="Aún no hay documentos internos de DTHS para este colaborador" />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {docs.map((doc, i) => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 14, background: C.white, borderRadius: 10, padding: '14px 16px', border: `1px solid ${C.gray200}`, boxShadow: '0 1px 3px rgba(0,0,0,.04)', animation: `fadeUp .3s ease ${i * 40}ms both` }}>
              <div style={{ width: 40, height: 40, borderRadius: 8, background: `${C.blue}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FileText size={18} color={C.blue} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: C.gray800, overflowWrap: 'anywhere' }}>{doc.nombre}</p>
                {doc.descripcion && <p style={{ margin: '2px 0 0', fontSize: 12, color: C.gray600, overflowWrap: 'anywhere' }}>{doc.descripcion}</p>}
                <p style={{ margin: '2px 0 0', fontSize: 11, color: C.gray600 }}>
                  {fmtFecha(doc.fechaSubida)} · {fmtBytes(doc.tamano)}{doc.subidoPor ? ` · Subido por ${doc.subidoPor}` : ''}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => abrir(doc)} title="Ver / descargar" style={{ padding: 7, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, color: C.blue, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Eye size={14} /></button>
                <button onClick={() => eliminar(doc)} title="Eliminar" style={{ padding: 7, background: `${C.red}12`, border: `1px solid ${C.red}33`, borderRadius: 7, color: C.red, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TAB: EMERGENCIA (RRHH)
════════════════════════════════════════════════════════════ */
const TabEmergenciaRRHH = ({ empleadoId, contacto, error, onSaved }) => {
  const [editMode,  setEditMode]  = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [toast,     setToast]     = useState(null);
  const [f, setF] = useState({
    nombreContacto:   contacto?.NombreContacto   || '',
    parentesco:       contacto?.Parentesco       || '',
    telefonoContacto: contacto?.telefonoContacto || '',
  });

  useEffect(() => {
    setF({
      nombreContacto:   contacto?.NombreContacto   || '',
      parentesco:       contacto?.Parentesco       || '',
      telefonoContacto: contacto?.telefonoContacto || '',
    });
  }, [contacto]);

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const msg = (tipo, texto) => setToast({ tipo, texto });
  const PARENTESCO = ['Cónyuge','Hijo/a','Padre','Madre','Hermano/a','Amigo/a','Familiar','Otro'];

  const guardar = async () => {
    setGuardando(true);
    try {
      await apiFetch(`/empleados/${empleadoId}/contacto-emergencia`, { method: 'PUT', body: JSON.stringify(f) });
      msg('success', 'Contacto de emergencia actualizado');
      setEditMode(false);
      onSaved?.();
    } catch (e) { msg('error', e.message); } finally { setGuardando(false); }
  };

  if (error) return <ErrorPanel mensaje={error} endpoint={`/empleados/:id/contacto-emergencia`} />;

  return (
    <div style={{ animation: 'fadeUp .25s ease' }}>
      {toast && <Toast tipo={toast.tipo} texto={toast.texto} onClose={() => setToast(null)} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, gap: 8 }}>
        {editMode ? (
          <>
            <button onClick={() => setEditMode(false)} style={{ padding: '8px 16px', background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.gray600 }}>Cancelar</button>
            <button onClick={guardar} disabled={guardando} style={{ padding: '8px 16px', background: C.green, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Save size={13} />{guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </>
        ) : (
          <button onClick={() => setEditMode(true)} style={{ padding: '8px 16px', background: C.navy, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Edit size={13} />Editar contacto
          </button>
        )}
      </div>
      <SeccionCard title="Contacto de Emergencia" icon={Heart}>
        {editMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div>
              <label style={LBL}>Nombre Completo</label>
              <input value={f.nombreContacto} onChange={e => upd('nombreContacto', e.target.value)} style={INP} placeholder="Nombre del contacto" />
            </div>
            <div>
              <label style={LBL}>Parentesco / Relación</label>
              <select value={f.parentesco} onChange={e => upd('parentesco', e.target.value)} style={SEL_FULL}>
                <option value="">Seleccionar</option>
                {PARENTESCO.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>Teléfono de Contacto</label>
              <input value={f.telefonoContacto} onChange={e => upd('telefonoContacto', e.target.value)} style={INP} placeholder="(809) 000-0000" type="tel" />
            </div>
          </div>
        ) : contacto ? (
          <Grid cols={2}>
            <Dato label="Nombre Completo"       value={contacto.NombreContacto} />
            <Dato label="Parentesco / Relación"  value={contacto.Parentesco} />
            <Dato label="Teléfono de Contacto"  value={contacto.telefonoContacto} />
          </Grid>
        ) : (
          <EmptyState icon={Heart} title="Sin contacto registrado" desc="Este colaborador no tiene un contacto de emergencia registrado aún" />
        )}
      </SeccionCard>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TAB: FICHA DEL EMPLEADO (RRHH)
════════════════════════════════════════════════════════════ */
const TabFichaRRHH = ({ empleado, catalogos, onSaved }) => {
  const [editMode,   setEditMode]   = useState(false);
  const [guardando,  setGuardando]  = useState(false);
  const [toast,      setToast]      = useState(null);
  const [hijos,      setHijos]      = useState([]);
  const [cargando,   setCargando]   = useState(true);
  const [fichaError, setFichaError] = useState(null);
  const [addingHijo, setAddingHijo] = useState(false);
  const [nuevoHijo,  setNuevoHijo]  = useState({ nombre: '', sexo: '', fechaNacimiento: '' });

  const [f, setF] = useState({ fechaNacimiento: '', sexo: '', genero: '', estadoCivil: '' });

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const msg = (tipo, texto) => setToast({ tipo, texto });

  useEffect(() => { loadFicha(); }, [empleado?.id]);

  const loadFicha = async () => {
    setCargando(true);
    setFichaError(null);
    try {
      const d = await apiFetch(`/empleados/${empleado.id}/ficha`);
      if (d.ficha) {
        setF({
          fechaNacimiento: d.ficha.fechaNacimiento || '',
          sexo:            d.ficha.sexo            || '',
          genero:          d.ficha.genero          || '',
          estadoCivil:     d.ficha.estadoCivil     || '',
        });
      }
      setHijos(d.hijos || []);
    } catch (e) {
      setFichaError(e.message);
    } finally {
      setCargando(false);
    }
  };

  const calcEdad = (fechaNac) => {
    if (!fechaNac) return null;
    const [y, m, d] = fechaNac.split('-').map(Number);
    const hoy = new Date();
    return hoy.getFullYear() - y -
      (hoy.getMonth() + 1 < m || (hoy.getMonth() + 1 === m && hoy.getDate() < d) ? 1 : 0);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await apiFetch(`/empleados/${empleado.id}/ficha`, { method: 'PUT', body: JSON.stringify(f) });
      msg('success', 'Ficha actualizada correctamente');
      setEditMode(false);
      onSaved?.();
      await loadFicha();
    } catch (e) { msg('error', e.message); } finally { setGuardando(false); }
  };

  const agregarHijo = async () => {
    if (!nuevoHijo.fechaNacimiento) { msg('error', 'La fecha de nacimiento del hijo/a es requerida'); return; }
    try {
      await apiFetch(`/empleados/${empleado.id}/hijos`, { method: 'POST', body: JSON.stringify(nuevoHijo) });
      msg('success', 'Hijo/a agregado correctamente');
      setAddingHijo(false);
      setNuevoHijo({ nombre: '', sexo: '', fechaNacimiento: '' });
      await loadFicha();
    } catch (e) { msg('error', e.message); }
  };

  const eliminarHijo = async (id) => {
    if (!window.confirm('¿Eliminar este dependiente del registro?')) return;
    try {
      await apiFetch(`/empleados/${empleado.id}/hijos/${id}`, { method: 'DELETE' });
      msg('success', 'Registro eliminado');
      await loadFicha();
    } catch (e) { msg('error', e.message); }
  };

  const sexos        = catalogos?.sexos?.length        ? catalogos.sexos        : ['Masculino', 'Femenino', 'No especificado'];
  const generos      = catalogos?.generos?.length      ? catalogos.generos      : ['Masculino', 'Femenino', 'No binario', 'Prefiero no decir'];
  const estadosCivil = catalogos?.estadosCivil?.length ? catalogos.estadosCivil : ['Soltero/a', 'Casado/a', 'Divorciado/a', 'Viudo/a', 'Unión Libre'];
  const edad = calcEdad(f.fechaNacimiento);

  if (cargando) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
      <div style={{ width: 32, height: 32, border: `3px solid ${C.gray200}`, borderTop: `3px solid ${C.blue}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  if (fichaError) return <ErrorPanel mensaje={fichaError} endpoint="/empleados/:id/ficha" />;

  return (
    <div style={{ animation: 'fadeUp .25s ease', display: 'grid', gap: 16 }}>
      {toast && <Toast tipo={toast.tipo} texto={toast.texto} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {editMode ? (
          <>
            <button onClick={() => setEditMode(false)} style={{ padding: '8px 16px', background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.gray600 }}>Cancelar</button>
            <button onClick={guardar} disabled={guardando} style={{ padding: '8px 16px', background: C.blue, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Save size={13} />{guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </>
        ) : (
          <button onClick={() => setEditMode(true)} style={{ padding: '8px 16px', background: C.navy, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Edit size={13} />Editar ficha
          </button>
        )}
      </div>

      <SeccionCard title="Datos Personales" icon={User}>
        {editMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div>
              <label style={LBL}>Fecha de Nacimiento</label>
              <input type="date" value={f.fechaNacimiento || ''} onChange={e => upd('fechaNacimiento', e.target.value)} style={INP} />
            </div>
            <div>
              <label style={LBL}>Sexo</label>
              <select value={f.sexo || ''} onChange={e => upd('sexo', e.target.value)} style={SEL_FULL}>
                <option value="">Seleccionar</option>
                {sexos.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>Género</label>
              <select value={f.genero || ''} onChange={e => upd('genero', e.target.value)} style={SEL_FULL}>
                <option value="">Seleccionar</option>
                {generos.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>Estado Civil</label>
              <select value={f.estadoCivil || ''} onChange={e => upd('estadoCivil', e.target.value)} style={SEL_FULL}>
                <option value="">Seleccionar</option>
                {estadosCivil.map(ec => <option key={ec} value={ec}>{ec}</option>)}
              </select>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.gray400, textTransform: 'uppercase', letterSpacing: .5 }}>FECHA DE NACIMIENTO</span>
                <Lock size={10} color={C.gray400} />
              </div>
              <div style={{ background: C.gray50, border: `1px solid ${C.gray200}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 600, color: C.gray800 }}>
                {f.fechaNacimiento ? fmtFecha(f.fechaNacimiento) : '—'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.gray400, textTransform: 'uppercase', letterSpacing: .5 }}>SEXO</span>
              <div style={{ background: C.gray50, border: `1px solid ${C.gray200}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 600, color: C.gray800 }}>{f.sexo || '—'}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.gray400, textTransform: 'uppercase', letterSpacing: .5 }}>EDAD</span>
                <Lock size={10} color={C.gray400} />
              </div>
              <div style={{ background: C.gray50, border: `1px solid ${C.gray200}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 600, color: C.gray800 }}>
                {edad != null ? `${edad} años` : '—'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.gray400, textTransform: 'uppercase', letterSpacing: .5 }}>GÉNERO</span>
              <div style={{ background: C.gray50, border: `1px solid ${C.gray200}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 600, color: C.gray800 }}>{f.genero || '—'}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.gray400, textTransform: 'uppercase', letterSpacing: .5 }}>ESTADO CIVIL</span>
              <div style={{ background: C.gray50, border: `1px solid ${C.gray200}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 600, color: C.gray800 }}>{f.estadoCivil || '—'}</div>
            </div>
          </div>
        )}
      </SeccionCard>

      <SeccionCard title="Hijos / Dependientes" icon={Baby}>
        <div style={{ marginBottom: 14 }}>
          {!addingHijo ? (
            <button onClick={() => setAddingHijo(true)} style={{ padding: '7px 14px', background: C.blue, border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={12} />Agregar hijo/a
            </button>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 180px auto', gap: 10, alignItems: 'flex-end', background: C.gray50, padding: 14, borderRadius: 10, border: `1px solid ${C.gray200}` }}>
              <div>
                <label style={LBL}>Nombre</label>
                <input value={nuevoHijo.nombre} onChange={e => setNuevoHijo(p => ({ ...p, nombre: e.target.value }))} style={INP} placeholder="Nombre completo" />
              </div>
              <div>
                <label style={LBL}>Sexo</label>
                <select value={nuevoHijo.sexo} onChange={e => setNuevoHijo(p => ({ ...p, sexo: e.target.value }))} style={SEL_FULL}>
                  <option value="">—</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                </select>
              </div>
              <div>
                <label style={LBL}>Fecha Nacimiento *</label>
                <input type="date" value={nuevoHijo.fechaNacimiento} onChange={e => setNuevoHijo(p => ({ ...p, fechaNacimiento: e.target.value }))} style={INP} />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={agregarHijo} style={{ padding: '9px 14px', background: C.green, border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: C.white }}>Guardar</button>
                <button onClick={() => { setAddingHijo(false); setNuevoHijo({ nombre: '', sexo: '', fechaNacimiento: '' }); }} style={{ padding: '9px 14px', background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: C.gray600 }}>Cancelar</button>
              </div>
            </div>
          )}
        </div>
        {hijos.length === 0 ? (
          <EmptyState icon={Baby} title="Sin dependientes registrados" desc="No hay hijos/as registrados para este colaborador" />
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {hijos.map((h, i) => {
              const edadH = calcEdad(h.fechaNacimiento);
              return (
                <div key={h.id || i} style={{ display: 'flex', alignItems: 'center', gap: 14, background: C.gray50, borderRadius: 10, padding: '12px 16px', border: `1px solid ${C.gray200}`, animation: `fadeUp .3s ease ${i * 40}ms both` }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: `${C.purple}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Baby size={17} color={C.purple} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: C.gray800 }}>{h.nombre || 'Sin nombre'}</p>
                    <p style={{ margin: 0, fontSize: 12, color: C.gray600 }}>
                      {h.sexo || '—'} · {fmtFecha(h.fechaNacimiento)}{edadH != null ? ` · ${edadH} año${edadH !== 1 ? 's' : ''}` : ''}
                    </p>
                  </div>
                  <button onClick={() => eliminarHijo(h.id)} title="Eliminar" style={{ padding: 7, background: `${C.red}12`, border: `1px solid ${C.red}33`, borderRadius: 7, color: C.red, cursor: 'pointer', display: 'flex' }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </SeccionCard>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TAB: HISTORIAL LABORAL
════════════════════════════════════════════════════════════ */
const TIPO_COLOR = {
  'CAMBIO_PUESTO':  { dot: '#2563eb', bg: '#eff6ff', border: '#93c5fd', label: 'Cambio de Puesto' },
  'CAMBIO_DEPTO':   { dot: '#d97706', bg: '#fffbeb', border: '#fcd34d', label: 'Cambio de Depto.' },
  'PROMOCION':      { dot: '#059669', bg: '#ecfdf5', border: '#6ee7b7', label: 'Promoción' },
  'TERMINACION':    { dot: '#dc2626', bg: '#fef2f2', border: '#fca5a5', label: 'Terminación' },
  'CONTRATACION':   { dot: '#7c3aed', bg: '#f5f3ff', border: '#c4b5fd', label: 'Contratación' },
  'AJUSTE_SALARIO': { dot: '#0891b2', bg: '#ecfeff', border: '#67e8f9', label: 'Ajuste Salarial' },
  default:          { dot: '#64748b', bg: '#f8fafc', border: '#cbd5e1', label: 'Acción' },
};

const TabHistorial = ({ historial, error }) => {
  const [expanded, setExpanded] = useState(null);
  const parseJSON = (str) => { try { return str ? JSON.parse(str) : {}; } catch { return {}; } };

  if (error) return <ErrorPanel mensaje={error} endpoint="/empleados/:id/acciones" />;
  if (historial.length === 0) return <EmptyState icon={Briefcase} title="Sin movimientos" desc="No hay acciones de personal registradas" />;

  return (
    <div style={{ animation: 'fadeUp .25s ease', position: 'relative', paddingLeft: 28 }}>
      <div style={{ position: 'absolute', left: 11, top: 8, bottom: 8, width: 2, background: C.gray200, borderRadius: 2 }} />
      {historial.map((item, i) => {
        const tipo = item.TipoAccion || item.tipoAccion || 'default';
        const c = TIPO_COLOR[tipo] || TIPO_COLOR.default;
        const isOpen = expanded === i;
        const ant  = parseJSON(item.DatosAnteriores || item.datosAnteriores);
        const nuev = parseJSON(item.DatosNuevos     || item.datosNuevos);
        return (
          <div key={i} style={{ position: 'relative', marginBottom: 14, animation: `fadeUp .3s ease ${i * 50}ms both` }}>
            <div style={{ position: 'absolute', left: -22, top: 16, width: 14, height: 14, borderRadius: '50%', background: c.dot, border: '3px solid white', boxShadow: `0 0 0 3px ${c.border}` }} />
            <div style={{ background: C.white, border: `1px solid ${c.border}`, borderRadius: 10, marginLeft: 8, overflow: 'hidden' }}>
              <button onClick={() => setExpanded(isOpen ? null : i)} style={{ width: '100%', background: c.bg, border: 'none', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ padding: '2px 10px', background: c.dot, color: C.white, borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{c.label}</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: c.dot }}>{item.Justificacion || item.justificacion || c.label}</p>
                    <p style={{ margin: 0, fontSize: 11, color: C.gray600 }}>{fmtFecha(item.FechaEfectiva || item.fechaAccion)} · {item.RealizadaPor || item.aprobadoPor || 'RRHH'}</p>
                  </div>
                </div>
                <ChevronRight size={15} color={C.gray400} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }} />
              </button>
              {isOpen && (
                <div style={{ padding: '14px 16px', borderTop: `1px solid ${c.border}`, animation: 'fadeUp .2s ease' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                    {(ant.cargo  || ant.cargoAnterior)          && <Dato label="Cargo Anterior"  value={ant.cargo  || ant.cargoAnterior} />}
                    {(nuev.nuevoCargo || nuev.cargo)             && <Dato label="Cargo Nuevo"     value={nuev.nuevoCargo || nuev.cargo} />}
                    {(ant.departamento || ant.depAnterior)       && <Dato label="Depto. Anterior" value={ant.departamento || ant.depAnterior} />}
                    {(nuev.nuevoDepartamento || nuev.departamento)&& <Dato label="Depto. Nuevo"   value={nuev.nuevoDepartamento || nuev.departamento} />}
                    {ant.salario                                  && <Dato label="Salario Anterior" value={fmtMoney(ant.salario)} />}
                    {nuev.nuevoSalario                            && <Dato label="Salario Nuevo"    value={fmtMoney(nuev.nuevoSalario)} />}
                    {(item.Estado || item.estado)                 && <Dato label="Estado"          value={item.Estado || item.estado} />}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TAB: VACACIONES
════════════════════════════════════════════════════════════ */
const VACA_ESTADO = {
  'Aprobada':  { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  'Pendiente': { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  'Rechazada': { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  'En Proceso':{ bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
};

const TabVacaciones = ({ solicitudes, balances, error }) => {
  const [view, setView] = useState('solicitudes');
  if (error) return <ErrorPanel mensaje={error} endpoint="/empleados/:id/vacaciones" />;
  return (
    <div style={{ animation: 'fadeUp .25s ease' }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.gray100, borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[{ id: 'solicitudes', label: 'Solicitudes' }, { id: 'balances', label: 'Balances' }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, cursor: 'pointer', background: view === v.id ? C.white : 'transparent', color: view === v.id ? C.blue : C.gray600, fontWeight: view === v.id ? 700 : 500, fontSize: 13, boxShadow: view === v.id ? '0 1px 3px rgba(0,0,0,.1)' : 'none', transition: 'all .15s' }}>{v.label}</button>
        ))}
      </div>
      {view === 'balances' && (
        balances.length === 0 ? <EmptyState icon={Calendar} title="Sin balances" desc="No hay balances de vacaciones registrados" /> :
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {balances.map((b, i) => (
            <div key={i} style={{ background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 12, padding: 16 }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: C.gray600, textTransform: 'uppercase', letterSpacing: .5 }}>Vacaciones {b.Anio || b.anio || ''}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ margin: 0, fontSize: 28, fontWeight: 800, color: C.blue }}>{b.DiasDisponibles ?? b.disponibles ?? 0}</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.gray600 }}>días disponibles</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: 0, fontSize: 13, color: C.green,  fontWeight: 600 }}>{b.DiasUsados    ?? b.usados    ?? 0} usados</p>
                  <p style={{ margin: 0, fontSize: 13, color: C.gold,   fontWeight: 600 }}>{b.DiasPendientes ?? b.pendientes ?? 0} pendientes</p>
                </div>
              </div>
              <div style={{ marginTop: 10, height: 6, background: C.gray100, borderRadius: 20, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: C.blue, borderRadius: 20, width: `${Math.min(100, ((b.DiasUsados ?? 0) / (b.DiasTotales ?? 1)) * 100)}%` }} />
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: C.gray600 }}>{b.DiasUsados ?? 0} de {b.DiasTotales ?? 0} días usados</p>
            </div>
          ))}
        </div>
      )}
      {view === 'solicitudes' && (
        solicitudes.length === 0 ? <EmptyState icon={Calendar} title="Sin solicitudes" desc="No hay solicitudes de vacaciones registradas" /> :
        <div style={{ display: 'grid', gap: 10 }}>
          {solicitudes.map((s, i) => {
            const est = s.Estado || s.estado || 'Pendiente';
            const ec  = VACA_ESTADO[est] || { bg: C.gray100, color: C.gray600, border: C.gray200 };
            return (
              <div key={i} style={{ background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 10, padding: '14px 16px', animation: `fadeUp .3s ease ${i * 40}ms both` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: C.gray800 }}>{s.TipoSolicitud || s.TipoPermiso || s.tipo || 'Vacaciones'}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: C.gray600 }}>{fmtFecha(s.FechaInicio || s.fechaInicio)} → {fmtFecha(s.FechaFin || s.fechaFin)}{(s.Dias || s.DiasSolicitados || s.dias) && ` · ${s.Dias || s.DiasSolicitados || s.dias} días`}</p>
                  </div>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: ec.bg, color: ec.color, border: `1px solid ${ec.border}` }}>{est}</span>
                </div>
                {(s.Motivo || s.motivo || s.Observaciones || s.observaciones) && (
                  <p style={{ margin: 0, fontSize: 12, color: C.gray600, background: C.gray50, borderRadius: 6, padding: '6px 10px' }}>{s.Motivo || s.motivo || s.Observaciones || s.observaciones}</p>
                )}
                <p style={{ margin: '8px 0 0', fontSize: 11, color: C.gray400 }}>Solicitado el {fmtFecha(s.FechaSolicitud || s.fechaSolicitud)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TAB: LICENCIAS & PERMISOS
════════════════════════════════════════════════════════════ */
const TabPermisos = ({ permisos, balancePermisos = [], error }) => {
  const [view, setView] = useState('solicitudes');
  const TIPO_PERMISO = {
    'Permiso Personal':    { color: C.blue,   bg: '#eff6ff' },
    'Permiso Médico':      { color: C.green,  bg: '#ecfdf5' },
    'Licencia Maternidad': { color: C.purple, bg: '#f5f3ff' },
    'Licencia Paternidad': { color: C.sky,    bg: '#f0f9ff' },
    'Permiso Luto':        { color: C.gray600,bg: C.gray100 },
    'Licencia Sin Sueldo': { color: C.orange, bg: '#fff7ed' },
    'default':             { color: C.blue,   bg: '#eff6ff' },
  };
  if (error) return <ErrorPanel mensaje={error} endpoint="/empleados/:id/permisos" />;
  return (
    <div style={{ animation: 'fadeUp .25s ease' }}>
      {MOSTRAR_BALANCE_PERMISOS && balancePermisos.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.gray100, borderRadius: 10, padding: 4, width: 'fit-content' }}>
          {[{ id: 'solicitudes', label: 'Solicitudes' }, { id: 'balances', label: 'Saldos' }].map(v => (
            <button key={v.id} onClick={() => setView(v.id)} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, cursor: 'pointer', background: view === v.id ? C.white : 'transparent', color: view === v.id ? C.blue : C.gray600, fontWeight: view === v.id ? 700 : 500, fontSize: 13, boxShadow: view === v.id ? '0 1px 3px rgba(0,0,0,.1)' : 'none' }}>{v.label}</button>
          ))}
        </div>
      )}
      {MOSTRAR_BALANCE_PERMISOS && view === 'balances' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {balancePermisos.map((b, i) => (
            <div key={i} style={{ background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 12, padding: 16 }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: C.gray600, textTransform: 'uppercase', letterSpacing: .5 }}>{b.TipoPermiso} · {b.Anio}</p>
              <p style={{ margin: 0, fontSize: 28, fontWeight: 800, color: C.blue }}>{b.DiasDisponibles ?? 0}</p>
              <p style={{ margin: 0, fontSize: 11, color: C.gray600 }}>días disponibles</p>
              <div style={{ marginTop: 10, height: 6, background: C.gray100, borderRadius: 20, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: C.blue, borderRadius: 20, width: `${Math.min(100, ((b.DiasUsados ?? 0) / (b.DiasTotales || 1)) * 100)}%` }} />
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: C.gray600 }}>{b.DiasUsados ?? 0} de {b.DiasTotales ?? 0} usados</p>
            </div>
          ))}
        </div>
      )}
      {view === 'solicitudes' && (
        permisos.length === 0 ? <EmptyState icon={Shield} title="Sin licencias" desc="No hay licencias o permisos especiales registrados" /> :
        <div style={{ display: 'grid', gap: 12 }}>
          {permisos.map((p, i) => {
            const tipo = p.TipoSolicitud || p.TipoPermiso || p.tipo || 'default';
            const tc   = TIPO_PERMISO[tipo] || TIPO_PERMISO.default;
            const est  = p.Estado || p.estado || 'Pendiente';
            const ec   = VACA_ESTADO[est] || { bg: C.gray100, color: C.gray600, border: C.gray200 };
            return (
              <div key={i} style={{ background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 12, overflow: 'hidden', animation: `fadeUp .3s ease ${i * 40}ms both` }}>
                <div style={{ padding: '14px 16px', borderLeft: `4px solid ${tc.color}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: tc.bg, color: tc.color }}>{tipo}</span>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: ec.bg, color: ec.color, border: `1px solid ${ec.border}` }}>{est}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, fontSize: 12 }}>
                      <div><span style={{ color: C.gray400 }}>Inicio: </span><strong>{fmtFecha(p.FechaInicio || p.fechaInicio)}</strong></div>
                      <div><span style={{ color: C.gray400 }}>Fin: </span><strong>{fmtFecha(p.FechaFin || p.fechaFin)}</strong></div>
                      {(p.Dias || p.DiasHabiles || p.DiasSolicitados || p.dias) && <div><span style={{ color: C.gray400 }}>Días: </span><strong>{p.Dias || p.DiasSolicitados || p.dias}</strong></div>}
                    </div>
                    {(p.Motivo || p.motivo || p.Observaciones || p.observaciones) && <p style={{ margin: '8px 0 0', fontSize: 12, color: C.gray600 }}>{p.Motivo || p.motivo || p.Observaciones || p.observaciones}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TAB: FORMACIÓN
════════════════════════════════════════════════════════════ */
const TabFormacion = ({ postulacion, error }) => {
  if (error) return <ErrorPanel mensaje={error} endpoint="/empleados/:id/postulacion" />;
  if (!postulacion) return <EmptyState icon={Award} title="Sin datos de postulación" desc="Este empleado no tiene una postulación vinculada en el sistema" />;
  const bool = (v) => v === true || v === 1 ? 'Sí' : v === false || v === 0 ? 'No' : '—';
  return (
    <div style={{ animation: 'fadeUp .25s ease', display: 'grid', gap: 16 }}>
      <SeccionCard title="Formación Académica" icon={Award}>
        <Grid cols={2}>
          <Dato label="Nivel Educativo"          value={postulacion.NivelEducativo} />
          <Dato label="Institución"              value={postulacion.Institucion} />
          <Dato label="Título Obtenido"          value={postulacion.TituloObtenido} />
          <Dato label="Año Graduación"           value={postulacion.AnioGraduacion} />
          <Dato label="Idiomas"                  value={postulacion.Idiomas} />
          <Dato label="Software / Herramientas"  value={postulacion.SoftwareConocido} />
          <Dato label="Conocimientos Especiales" value={postulacion.ConocimientosEspeciales} full />
        </Grid>
      </SeccionCard>
      <SeccionCard title="Experiencia Laboral" icon={Briefcase}>
        <Grid cols={2}>
          <Dato label="Años de Experiencia"   value={postulacion.ExperienciaAnios ? `${postulacion.ExperienciaAnios} años` : null} />
          <Dato label="Último Empleador"      value={postulacion.UltimoEmpleador} />
          <Dato label="Último Puesto"         value={postulacion.UltimoPuesto} />
          <Dato label="Expectativa Salarial"  value={postulacion.ExpectativaSalarial ? `RD$ ${Number(postulacion.ExpectativaSalarial).toLocaleString('es-DO')}` : null} />
          <Dato label="Experiencia Detallada" value={postulacion.ExperienciaLaboral} full />
          <Dato label="Motivación"            value={postulacion.Motivacion} full />
        </Grid>
      </SeccionCard>
      <SeccionCard title="Disponibilidad & Referencias" icon={CheckCircle}>
        <Grid cols={2}>
          <Dato label="Disponibilidad Inmediata" value={bool(postulacion.DisponibilidadInmediata)} />
          <Dato label="Disponibilidad Viajar"    value={bool(postulacion.DisponibilidadViajar)} />
          <Dato label="Tiene Licencia"           value={bool(postulacion.TieneLicencia)} />
          <Dato label="Tipo Licencia"            value={postulacion.TipoLicencia} />
          <Dato label="Referencia 1"             value={postulacion.ReferenciaPersonal1} />
          <Dato label="Referencia 2"             value={postulacion.ReferenciaPersonal2} />
          <Dato label="Cómo conoció la vacante"  value={postulacion.ComoConocioVacante} />
        </Grid>
      </SeccionCard>
      <SeccionCard title="Datos de Postulación" icon={FileText}>
        <Grid cols={2}>
          <Dato label="Cargo Postulado"    value={postulacion.CargoPostulado} />
          <Dato label="Departamento"       value={postulacion.DepartamentoPostulado} />
          <Dato label="Fecha Postulación"  value={fmtFecha(postulacion.FechaPostulacion)} />
          <Dato label="Estado Postulación" value={postulacion.EstadoPostulacion} />
        </Grid>
      </SeccionCard>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   HELPERS UI
════════════════════════════════════════════════════════════ */
const SeccionCard = ({ title, icon: Icon, children }) => (
  <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.gray200}`, overflow: 'hidden' }}>
    <div style={{ padding: '14px 18px', background: C.gray50, borderBottom: `1px solid ${C.gray200}`, display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 28, height: 28, borderRadius: 6, background: `${C.blue}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={14} color={C.blue} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: C.gray800 }}>{title}</span>
    </div>
    <div style={{ padding: 18 }}>{children}</div>
  </div>
);

const Grid = ({ cols = 2, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${cols === 2 ? 200 : 140}px, 1fr))`, gap: 14 }}>
    {children}
  </div>
);

const Dato = ({ label, value, full }) => (
  <div style={{ gridColumn: full ? '1/-1' : undefined }}>
    <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: C.gray400, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>{label}</p>
    <p style={{ margin: 0, fontSize: 13, color: C.gray800, fontWeight: 500 }}>{value || <span style={{ color: C.gray200 }}>—</span>}</p>
  </div>
);

const EmptyState = ({ icon: Icon, title, desc }) => (
  <div style={{ textAlign: 'center', padding: '3rem', background: C.gray50, borderRadius: 12, border: `1px dashed ${C.gray200}` }}>
    <Icon size={40} style={{ opacity: .25, margin: '0 auto 10px', display: 'block', color: C.gray600 }} />
    <p style={{ margin: 0, fontWeight: 600, color: C.gray800, fontSize: 14 }}>{title}</p>
    <p style={{ margin: '4px 0 0', fontSize: 13, color: C.gray600 }}>{desc}</p>
  </div>
);

/* ════════════════════════════════════════════════════════════
   MÓDULO PRINCIPAL — LISTA DE EMPLEADOS
════════════════════════════════════════════════════════════ */
const EmpleadosModule = () => {
  const [empleados,     setEmpleados]     = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [puestos,       setPuestos]       = useState([]);
  const [filtros,       setFiltros]       = useState({ busqueda: '', departamento: '', estado: 'todos' });
  const [modalActivo,   setModalActivo]   = useState(null);
  const [empleadoSel,   setEmpleadoSel]   = useState(null);
  const [pagina,        setPagina]        = useState(1);
  const [cargando,      setCargando]      = useState(true);
  const [guardando,     setGuardando]     = useState(false);
  const [toast,         setToast]         = useState(null);
  const POR_PAG = 10;

  const msg = (tipo, texto) => setToast({ tipo, texto });

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [empRes, deptRes, puestosRes] = await Promise.all([
        apiFetch('/empleados/list'),
        apiFetch('/departamentos'),
        apiFetch('/puestos'),
      ]);

      const raw = Array.isArray(empRes) ? empRes : empRes.empleados || [];
      setEmpleados(raw.map(e => ({
        id:                  e.EmpleadoID,
        nombre:              e.NOMBRE          || e.Nombre    || '',
        apellido:            e.APELLIDO        || e.Apellido  || '',
        nombreCompleto:      `${e.NOMBRE || e.Nombre || ''} ${e.APELLIDO || e.Apellido || ''}`.trim(),
        email:               e.Email           || '',
        telefono:            e.Telefono        || '',
        telefono2:           e.Telefono2       || '',
        direccion:           e.Direccion       || '',
        direccion2:          e.Direccion2      || '',
        cedula:              e.CEDULA          || '',
        departamento:        e.DEPARTAMENTO_NOMBRE || 'Sin departamento',
        departamentoId:      e.DEPARTAMENTOID  || null,
        puesto:              e.NombrePuesto || e.PUESTO_NOMBRE || e.NOMBRE_PUESTO || e.PuestoNombre || e.CARGO || 'Sin puesto',
        puestoId:            e.PUESTOID        || null,
        fechaIngreso:        e.FECHAINGRESO    ? e.FECHAINGRESO.split('T')[0]    : '',
        fechaNacimiento:     e.FechaNacimiento ? e.FechaNacimiento.split('T')[0] : '',
        salario:             e.Salario         || 0,
        estado:              e.ESTADO === true || e.ESTADO === 1 ? 'activo' : 'inactivo',
        supervisorId:        e.SupervisorID       || null,
        supervisorInmediato: e.SupervisorInmediato || '',
        tipoContrato:        e.TipoContrato        || '',
        jornada:             e.Jornada             || '',
        turno:               e.Turno               || '',
        sexo:                e.Sexo                || '',   // ← restaurado
        genero:              e.Genero              || '',
        estadoCivil:         e.EstadoCivil         || '',
        emailPersonal:       e.EmailPersonal       || '',
        foto:                e.FotoUrl ? `${UPLOADS_URL}${e.FotoUrl}` : null,
      })));

      const depts = Array.isArray(deptRes) ? deptRes : deptRes.departamentos || [];
      setDepartamentos(depts.map(d => ({ id: d.DepartamentoID || d.id, nombre: d.Nombre || d.nombre })));

      const psts = Array.isArray(puestosRes) ? puestosRes : puestosRes.puestos || [];
      setPuestos(psts.map(p => ({ id: p.PuestoID || p.id, nombre: p.NOMBRE || p.Nombre || p.nombre, departamentoId: p.DepartamentoID ?? p.departamentoID ?? p.departamentoId })));
    } catch (e) {
      msg('error', `Error al cargar: ${e.message}`);
    } finally { setCargando(false); }
  };

  const actualizarEmpleado = async (data) => {
    setGuardando(true);
    try {
      await apiFetch(`/empleados/update/${data.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nombre: data.nombre, apellido: data.apellido, cedula: data.cedula,
          email: data.email, telefono: data.telefono, direccion: data.direccion,
          cargo: data.puesto, salario: parseFloat(data.salario) || 0,
          fechaIngreso: data.fechaIngreso, estado: data.estado === 'activo' ? 1 : 0,
          departamentoID: parseInt(data.departamentoId) || null,
          puestoID:       parseInt(data.puestoId)       || null,
          supervisorID:   parseInt(data.supervisorId)   || null,
          supervisorInmediato: data.supervisorInmediato || null,
        }),
      });
      msg('success', 'Empleado actualizado correctamente');
      await cargarDatos();
      setModalActivo(null);
      setEmpleadoSel(null);
    } catch (e) { msg('error', e.message); } finally { setGuardando(false); }
  };

  const eliminarEmpleado = async (id) => {
    if (!window.confirm('¿Eliminar este empleado? Esta acción no se puede deshacer.')) return;
    try {
      await apiFetch(`/empleados/delete/${id}`, { method: 'DELETE' });
      msg('success', 'Empleado eliminado');
      await cargarDatos();
    } catch (e) { msg('error', e.message); }
  };

  const filtrados = useMemo(() => {
    return empleados.filter(e => {
      const q  = filtros.busqueda.toLowerCase();
      const mB = !q || [e.nombre, e.apellido, e.nombreCompleto, e.email, e.cedula].some(f => f?.toLowerCase().includes(q));
      const mD = !filtros.departamento || e.departamento === filtros.departamento;
      const mE = filtros.estado === 'todos' || e.estado === filtros.estado;
      return mB && mD && mE;
    });
  }, [empleados, filtros]);

  const stats = useMemo(() => ({
    total:   empleados.length,
    activos: empleados.filter(e => e.estado === 'activo').length,
    deptos:  new Set(empleados.map(e => e.departamento).filter(d => d !== 'Sin departamento')).size,
    nuevos:  empleados.filter(e => {
      if (!e.fechaIngreso) return false;
      const [y, m] = e.fechaIngreso.split('-').map(Number);
      const h = new Date();
      return y === h.getFullYear() && m === h.getMonth() + 1;
    }).length,
  }), [empleados]);

  const totalPags = Math.ceil(filtrados.length / POR_PAG);
  const paginados = filtrados.slice((pagina - 1) * POR_PAG, pagina * POR_PAG);

  /* ─ FORMULARIO EDITAR ─ */
  const FormEditar = ({ emp, onSave, onCancel }) => {
    const [f, setF] = useState({
      id: emp.id, nombre: emp.nombre, apellido: emp.apellido, cedula: emp.cedula,
      email: emp.email, telefono: emp.telefono, direccion: emp.direccion,
      departamento: emp.departamento, departamentoId: emp.departamentoId,
      puesto: emp.puesto, puestoId: emp.puestoId,
      fechaIngreso: emp.fechaIngreso, salario: emp.salario,
      estado: emp.estado, supervisorId: emp.supervisorId, supervisorInmediato: emp.supervisorInmediato,
    });
    const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
    // Nota: hoy ningun Puesto tiene DepartamentoID asignado en la BD (dato
    // pendiente de reparar), asi que si el filtro por departamento da vacio
    // se muestran todos los puestos en vez de dejar el selector sin opciones.
    const puestosFiltrados = f.departamentoId ? puestos.filter(p => p.departamentoId === parseInt(f.departamentoId)) : puestos;
    const puestosDepto = puestosFiltrados.length > 0 ? puestosFiltrados : puestos;

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onCancel}>
        <div style={{ background: C.white, borderRadius: 14, maxWidth: 800, width: '100%', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.gray200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.gray50 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Edit size={18} color={C.blue} />
              <span style={{ fontWeight: 700, fontSize: 16, color: C.gray800 }}>Editar Empleado — {emp.nombreCompleto}</span>
            </div>
            <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.gray600, padding: 4, borderRadius: 6 }}><X size={20} /></button>
          </div>
          <div style={{ padding: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            <div>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: C.gray800, paddingBottom: 8, borderBottom: `2px solid ${C.gray200}` }}>Información Personal</h4>
              {[
                { label: 'Nombre *', key: 'nombre' }, { label: 'Apellido *', key: 'apellido' },
                { label: 'Cédula',   key: 'cedula'  }, { label: 'Email *',   key: 'email', type: 'email' },
                { label: 'Teléfono', key: 'telefono', type: 'tel' },
              ].map(field => (
                <div key={field.key} style={{ marginBottom: 14 }}>
                  <label style={LBL}>{field.label}</label>
                  <input type={field.type || 'text'} value={f[field.key] || ''} onChange={e => upd(field.key, e.target.value)} style={INP} />
                </div>
              ))}
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Dirección</label>
                <textarea value={f.direccion || ''} onChange={e => upd('direccion', e.target.value)} style={{ ...INP, minHeight: 80, resize: 'vertical' }} />
              </div>
            </div>
            <div>
              <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: C.gray800, paddingBottom: 8, borderBottom: `2px solid ${C.gray200}` }}>Información Laboral</h4>
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Departamento *</label>
                <SearchableSelect
                  value={f.departamentoId}
                  onChange={v => {
                    const id = v ? parseInt(v) : null;
                    const d = departamentos.find(d => d.id === id);
                    upd('departamentoId', id); upd('departamento', d?.nombre || '');
                    upd('puestoId', null); upd('puesto', '');
                  }}
                  options={departamentos.map(d => ({ id: d.id, label: d.nombre }))}
                  placeholder="Buscar departamento..."
                  emptyLabel="Seleccionar"
                  inputStyle={SEL}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Puesto</label>
                <SearchableSelect
                  value={f.puestoId}
                  onChange={v => {
                    const id = v ? parseInt(v) : null;
                    const p = puestos.find(p => p.id === id);
                    upd('puestoId', id); upd('puesto', p?.nombre || '');
                  }}
                  options={puestosDepto.map(p => ({ id: p.id, label: p.nombre }))}
                  placeholder="Buscar puesto..."
                  emptyLabel="Seleccionar"
                  disabled={!f.departamentoId}
                  inputStyle={{ ...SEL, opacity: !f.departamentoId ? .5 : 1 }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Supervisor Inmediato</label>
                <SearchableSelect
                  value={f.supervisorId}
                  onChange={v => {
                    const id = v ? parseInt(v) : null;
                    const s = empleados.find(em => em.id === id);
                    upd('supervisorId', id);
                    upd('supervisorInmediato', s ? `${s.nombre} ${s.apellido}`.trim() : '');
                  }}
                  options={empleados.filter(em => em.id !== f.id).sort((a, b) => a.nombre.localeCompare(b.nombre)).map(em => ({ id: em.id, label: `${em.nombre} ${em.apellido}` }))}
                  placeholder="Buscar supervisor..."
                  emptyLabel="Sin supervisor"
                  inputStyle={SEL}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Fecha de Ingreso</label>
                <input type="date" value={f.fechaIngreso || ''} onChange={e => upd('fechaIngreso', e.target.value)} style={INP} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Salario (RD$)</label>
                <input type="number" value={f.salario || ''} onChange={e => upd('salario', e.target.value)} style={INP} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={LBL}>Estado</label>
                <select value={f.estado} onChange={e => upd('estado', e.target.value)} style={SEL}>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.gray200}`, display: 'flex', justifyContent: 'flex-end', gap: 10, background: C.gray50 }}>
            <button onClick={onCancel} style={{ padding: '10px 20px', background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.gray700 }}>Cancelar</button>
            <button onClick={() => { if (!f.nombre || !f.apellido || !f.email) { msg('error', 'Completa los campos obligatorios'); return; } onSave(f); }} disabled={guardando}
              style={{ padding: '10px 20px', background: C.blue, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.white, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Save size={14} />{guardando ? 'Guardando...' : 'Actualizar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ─── RENDER PRINCIPAL ─── */
  if (cargando) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 16, color: C.gray600 }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${C.gray200}`, borderTop: `3px solid ${C.blue}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{'@keyframes spin { to { transform:rotate(360deg); } }'}</style>
      <span style={{ fontSize: 14 }}>Cargando empleados...</span>
    </div>
  );

  return (
    <div style={{ padding: 24, background: C.gray50, minHeight: '100vh', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <style>{`
        @keyframes spin    { to { transform:rotate(360deg); } }
        @keyframes fadeUp  { from { transform:translateY(10px); opacity:0; } to { transform:translateY(0); opacity:1; } }
        input:focus, select:focus, textarea:focus { outline:none; border-color:${C.blue} !important; box-shadow:0 0 0 3px ${C.blue}22; }
        button:hover:not(:disabled) { opacity:.88; }
        button:disabled { opacity:.5; cursor:not-allowed; }
      `}</style>

      {toast && <Toast tipo={toast.tipo} texto={toast.texto} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: C.gray800, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Users size={28} color={C.blue} /> Gestión de Empleados
          </h1>
          <p style={{ margin: '4px 0 0', color: C.gray600, fontSize: 13 }}>{empleados.length} colaboradores registrados en el sistema</p>
        </div>
        <button onClick={cargarDatos} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.gray700 }}>
          <RefreshCw size={14} />Actualizar
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Empleados', value: stats.total,   color: C.blue,   bg: '#dbeafe', icon: Users },
          { label: 'Activos',         value: stats.activos, color: C.green,  bg: '#dcfce7', icon: CheckCircle },
          { label: 'Departamentos',   value: stats.deptos,  color: C.purple, bg: '#ede9fe', icon: Building },
          { label: 'Nuevos este Mes', value: stats.nuevos,  color: C.orange, bg: '#ffedd5', icon: Star },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} style={{ background: C.white, padding: '20px 22px', borderRadius: 12, border: `1px solid ${C.gray200}`, boxShadow: '0 1px 3px rgba(0,0,0,.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: '0 0 6px', fontSize: 12, color: C.gray600, fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5 }}>{s.label}</p>
                <p style={{ margin: 0, fontSize: 28, fontWeight: 800, color: s.color, letterSpacing: '-1px' }}>{s.value}</p>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={22} color={s.color} />
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.gray200}`, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.gray400 }} />
          <input type="text" placeholder="Buscar por nombre, email, cédula..." value={filtros.busqueda}
            onChange={e => { setFiltros({ ...filtros, busqueda: e.target.value }); setPagina(1); }}
            style={{ width: '100%', padding: '9px 9px 9px 36px', border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <select value={filtros.departamento} onChange={e => { setFiltros({ ...filtros, departamento: e.target.value }); setPagina(1); }} style={SEL}>
          <option value="">Todos los departamentos</option>
          {departamentos.map(d => <option key={d.id} value={d.nombre}>{d.nombre}</option>)}
        </select>
        <select value={filtros.estado} onChange={e => { setFiltros({ ...filtros, estado: e.target.value }); setPagina(1); }} style={SEL}>
          <option value="todos">Todos los estados</option>
          <option value="activo">Activos</option>
          <option value="inactivo">Inactivos</option>
        </select>
        {(filtros.busqueda || filtros.departamento || filtros.estado !== 'todos') && (
          <button onClick={() => { setFiltros({ busqueda: '', departamento: '', estado: 'todos' }); setPagina(1); }} style={{ padding: '9px 14px', background: C.gray100, border: `1px solid ${C.gray200}`, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: C.gray700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <X size={13} />Limpiar
          </button>
        )}
      </div>

      <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.gray200}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.gray50, borderBottom: `2px solid ${C.gray100}` }}>
                {['Colaborador', 'Departamento', 'Puesto', 'Estado', 'Ingreso', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.gray400, textTransform: 'uppercase', letterSpacing: .7, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginados.map((emp) => {
                const initials = `${emp.nombre?.charAt(0) || ''}${emp.apellido?.charAt(0) || ''}`;
                return (
                  <tr key={emp.id} style={{ borderBottom: `1px solid ${C.gray100}`, transition: 'background .15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = C.gray50}
                    onMouseLeave={e => e.currentTarget.style.background = C.white}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 38, height: 38, borderRadius: '50%', overflow: 'hidden', background: `linear-gradient(135deg, ${C.sky}, ${C.blue})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: C.white, flexShrink: 0 }}>
                          {emp.foto ? <img src={emp.foto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: C.gray800 }}>{emp.nombreCompleto}</p>
                          <p style={{ margin: 0, fontSize: 12, color: C.gray400 }}>{emp.email}</p>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.gray700 }}>{emp.departamento}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.gray700 }}>{emp.puesto}</td>
                    <td style={{ padding: '14px 16px' }}><EstadoBadge activo={emp.estado === 'activo'} /></td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: C.gray600, whiteSpace: 'nowrap' }}>{fmtFecha(emp.fechaIngreso)}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button title="Ver Expediente" onClick={() => { setEmpleadoSel(emp); setModalActivo('expediente'); }} style={{ padding: 7, background: '#eff6ff', border: `1px solid #dbeafe`, borderRadius: 7, cursor: 'pointer', color: C.blue, display: 'flex' }}><Eye size={14} /></button>
                        <button title="Editar"         onClick={() => { setEmpleadoSel(emp); setModalActivo('editar'); }}     style={{ padding: 7, background: '#f0fdf4', border: `1px solid #dcfce7`, borderRadius: 7, cursor: 'pointer', color: C.green, display: 'flex' }}><Edit size={14} /></button>
                        <button title="Eliminar"       onClick={() => eliminarEmpleado(emp.id)}                               style={{ padding: 7, background: '#fef2f2', border: `1px solid #fecaca`, borderRadius: 7, cursor: 'pointer', color: C.red, display: 'flex' }}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPags > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: `1px solid ${C.gray100}`, background: C.gray50 }}>
            <span style={{ fontSize: 13, color: C.gray600 }}>{((pagina - 1) * POR_PAG) + 1}–{Math.min(pagina * POR_PAG, filtrados.length)} de {filtrados.length}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setPagina(Math.max(1, pagina - 1))} disabled={pagina === 1} style={PGBTN}>Anterior</button>
              {Array.from({ length: totalPags }, (_, i) => i + 1).slice(Math.max(0, pagina - 3), pagina + 2).map(n => (
                <button key={n} onClick={() => setPagina(n)} style={{ ...PGBTN, background: pagina === n ? C.blue : C.white, color: pagina === n ? C.white : C.gray700, borderColor: pagina === n ? C.blue : C.gray200 }}>{n}</button>
              ))}
              <button onClick={() => setPagina(Math.min(totalPags, pagina + 1))} disabled={pagina === totalPags} style={PGBTN}>Siguiente</button>
            </div>
          </div>
        )}
      </div>

      {modalActivo === 'editar' && empleadoSel && (
        <FormEditar emp={empleadoSel} onSave={actualizarEmpleado} onCancel={() => { setModalActivo(null); setEmpleadoSel(null); }} />
      )}
      {modalActivo === 'expediente' && empleadoSel && (
        <ExpedienteDrawer empleado={empleadoSel} onClose={() => { setModalActivo(null); setEmpleadoSel(null); }} departamentos={departamentos} puestos={puestos} empleados={empleados} onSaved={cargarDatos} />
      )}
    </div>
  );
};

/* ─── Style constants ─── */
const LBL    = { display: 'block', fontSize: 12, fontWeight: 600, color: C.gray600, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 };
const INP    = { width: '100%', padding: '9px 12px', border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const SEL    = { padding: '9px 12px', border: `1px solid ${C.gray200}`, borderRadius: 8, fontSize: 13, background: C.white, cursor: 'pointer', fontFamily: 'inherit' };
const SEL_FULL = { ...SEL, width: '100%', boxSizing: 'border-box' };
const PGBTN  = { padding: '7px 12px', border: `1px solid ${C.gray200}`, borderRadius: 7, background: C.white, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: C.gray700 };

export default EmpleadosModule;
